import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';
import { buildOrgById, isForkedPath, ownerAbove, ownerOfUnit, pathDepthOf } from '../lib/pathBuilder';
import type { DesignerPath, OrgPathNode } from '../types';

interface PathFlowsPanelProps {
  tab: 'flows' | 'json';
  onTabChange: (tab: 'flows' | 'json') => void;
  paths: DesignerPath[];
  orgNodes: OrgPathNode[];
  jsonPreview: string;
  /**
   * Take a stop off its path. This panel listed the stops and offered nothing to
   * do about them — zero controls — so a stop added by mistake could only be
   * undone by deleting the whole path and drawing it again. The canvas can
   * remove one by clicking it, but nothing here said so, and this is the list a
   * person reads when checking their work.
   */
  onRemoveStop?: (pathId: string, nodeId: string) => void;
  /** The cycle's owner, named at the top of every path and the last fallback for an ownerless stop. */
  cycleOwner?: CatchballParticipantRef | null;
}

export function PathFlowsPanel({ tab, onTabChange, paths, orgNodes, jsonPreview, onRemoveStop, cycleOwner = null }: PathFlowsPanelProps) {
  const orgById = buildOrgById(orgNodes);

  return (
    <aside
      style={{
        width: 280,
        flexShrink: 0,
        borderLeft: '1px solid var(--rule)',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-raised)',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', borderBottom: '1px solid var(--rule)' }}>
        {(['flows', 'json'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onTabChange(t)}
            style={{
              flex: 1,
              padding: '10px 12px',
              fontFamily: 'var(--mono)',
              fontSize: '0.68rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              background: 'none',
              border: 'none',
              borderBottom: tab === t ? '2px solid var(--mustard)' : '2px solid transparent',
              color: tab === t ? 'var(--mustard)' : 'var(--text-dim)',
              cursor: 'pointer',
            }}
          >
            {t === 'flows' ? 'Flows' : 'JSON'}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {tab === 'flows'
          ? paths.map((path) => <PathSummaryCard key={path.id} path={path} paths={paths} orgById={orgById} onRemoveStop={onRemoveStop} cycleOwner={cycleOwner} />)
          : (
            <pre
              style={{
                fontFamily: 'var(--mono)',
                fontSize: '0.78rem',
                color: 'var(--text-sec)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
              }}
            >
              {jsonPreview}
            </pre>
          )}
      </div>
    </aside>
  );
}

function PathSummaryCard({
  onRemoveStop,
  path,
  paths,
  orgById,
  cycleOwner,
}: {
  path: DesignerPath;
  paths: DesignerPath[];
  orgById: Map<string, OrgPathNode>;
  onRemoveStop?: (pathId: string, nodeId: string) => void;
  cycleOwner: CatchballParticipantRef | null;
}) {
  const nodeNames = path.nodes.map((id) => orgById.get(id)).filter(Boolean);
  const stopCount = nodeNames.length;
  // Levels are rows of the path, not stops: a path that forks has more stops
  // than levels.
  const depth = path.nodes.reduce((max, id) => Math.max(max, pathDepthOf(path, id, orgById) + 1), 0);
  const forked = isForkedPath(path, orgById);
  const depthLabel =
    (depth >= 4 ? 'Full depth' : depth === 3 ? '3 levels' : depth === 2 ? '2 levels' : 'Anchor only') +
    (forked ? ' · forks' : '');

  return (
    <div
      style={{
        border: '1px solid var(--rule-bright)',
        background: 'var(--bg)',
        padding: 10,
        marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <div style={{ width: 3, background: path.color, flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: '0.95rem', fontWeight: 500, color: 'var(--text-pri)' }}>{path.name}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>
            {stopCount} stops · {depthLabel}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {nodeNames.map((n, i) => (
          <div key={n!.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem', color: 'var(--text-sec)' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: i === 0 ? path.color : 'var(--rule-bright)' }} />
              {n!.name}
              <span style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text-dim)' }}>L{n!.relativeLevel}</span>
              {onRemoveStop && n!.relativeLevel !== 0 && (
                <button
                  type="button"
                  aria-label={`Remove ${n!.name} from ${path.name}`}
                  title={`Remove ${n!.name} from ${path.name}`}
                  onClick={() => onRemoveStop(path.id, n!.id)}
                  style={{
                    marginLeft: 'auto',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-dim)',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    lineHeight: 1,
                    padding: '2px 4px',
                  }}
                >
                  ×
                </button>
              )}
            </div>
            <StopOwner nodeId={n!.id} isAnchor={n!.relativeLevel === 0} paths={paths} orgById={orgById} cycleOwner={cycleOwner} />
            {i < nodeNames.length - 1 && (
              <div style={{ textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.68rem', padding: '2px 0' }}>↓</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Who answers for a stop: its own owner, or — named as such — the owner above
 * it, and at the top the cycle's owner. Every stop says someone, or says that
 * nobody does.
 */
function StopOwner({
  nodeId,
  isAnchor,
  paths,
  orgById,
  cycleOwner,
}: {
  nodeId: string;
  isAnchor: boolean;
  paths: DesignerPath[];
  orgById: Map<string, OrgPathNode>;
  cycleOwner: CatchballParticipantRef | null;
}) {
  const line = { fontSize: '0.78rem', paddingLeft: 11, marginTop: 1 } as const;
  if (isAnchor) {
    return cycleOwner ? (
      <div style={{ ...line, color: 'var(--text-ter)' }}>{cycleOwner.displayName} · cycle owner</div>
    ) : (
      <div style={{ ...line, color: 'var(--coral)', fontWeight: 600 }}>No cycle owner</div>
    );
  }
  const own = ownerOfUnit(nodeId, paths);
  if (own) return <div style={{ ...line, color: 'var(--text-ter)' }}>{own.displayName}</div>;
  const above = ownerAbove(nodeId, paths, orgById, cycleOwner);
  return (
    <div style={{ ...line, color: 'var(--text-dim)' }}>
      {above
        ? `No owner · ${above.ref.displayName} answers (${above.from ? `owner of ${orgById.get(above.from)?.name ?? 'the unit above'}` : 'cycle owner'})`
        : 'No owner, and no cycle owner above it'}
    </div>
  );
}
